import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, IconButton, Card, Accordion, AccordionSummary, AccordionDetails, useTheme, TextField, Select, MenuItem, FormControl, Button, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ArrowBackIosNewRounded, AddRounded, ExpandMoreRounded } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { between, colCenter } from '@ui/style/style';
import { Delete24Regular } from '@fluentui/react-icons';
import useLiveFeedStyles from './useLiveFeedStyles';
import { useDispatch, useSelector } from 'react-redux';
import { addCanvasFrameAndPolygon, deleteCanvasPolygon, getCanvasFrameAndPolygon, updateCanvasPolygon } from '@application/redux/polygon-canvas/polygon-thunk';
import { toast } from 'react-toastify';
import useAsyncEffect from 'use-async-effect';
import { getLiveStreamUrl } from '@application/redux/livestream/live-stream-slice';
import { clearCanvas } from '@application/redux/polygon-canvas/polygon-slice';
export const LiveFeed = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { assetId, systemId } = useParams();
    const dispatch = useDispatch();
    const { containerStyles, headerStyles, breadcrumbStyles, videoContainerStyles, videoBoxStyles, videoImageStyles, timestampStyles, zonesCardStyles, zonesHeaderStyles, addButtonStyles, zoneListStyles, accordionStyles, accordionSummaryStyles, accordionContentStyles, zoneInfoStyles, zoneRightSideStyles, colorBoxStyles, expandIconStyles, accordionDetailsStyles, detailRowStyles, detailLabelStyles, textFieldStyles, formControlStyles, selectStyles } = useLiveFeedStyles();
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const [expandedZone, setExpandedZone] = useState(false);
    // Simplified state - no complex local caching
    const [zones, setZones] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingZoneId, setEditingZoneId] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [tempPolygon, setTempPolygon] = useState([]);
    // Point editing states
    const [isEditingPoints, setIsEditingPoints] = useState(false);
    // Drag and drop states
    const [isDragging, setIsDragging] = useState(false);
    const [dragPointIndex, setDragPointIndex] = useState(null);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    // Only keep the current editing zone's data
    const [editingZoneData, setEditingZoneData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved changes
    const { liveStreamUrl } = useSelector((state) => state.LiveStreamUrl);
    const { canvas, polygons } = useSelector((state) => state.canvasFrame);
    const [liveImageUrl, setLiveImageUrl] = useState('');
    const [streamCheckInterval, setStreamCheckInterval] = useState(null);
    const [imageUpdateInterval, setImageUpdateInterval] = useState(null);
    // Generate a random image URL for fallback
    const generateRandomImageUrl = () => {
        const timestamp = Date.now();
        const randomId = Math.floor(Math.random() * 1000);
        return `https://picsum.photos/800/600?random=${randomId}&t=${timestamp}`;
    };
    // const [fallbackImageUrl, setFallbackImageUrl] = useState(generateRandomImageUrl());
    useAsyncEffect(() => {
        dispatch(getLiveStreamUrl({ systemId: systemId }));
    }, [systemId]);
    const generateImageUrl = (sasUrl) => {
        if (!sasUrl)
            return '';
        const separator = sasUrl.includes('?') ? '&' : '?';
        const timestamp = Date.now();
        return `${sasUrl}${separator}t=${timestamp}`;
    };
    // Format a zone type string to a normalized backend-friendly value.
    // This ensures consistency when building polygon names and when parsing them back.
    const formatZoneType = (type) => {
        if (!type)
            return 'normal';
        const t = type.toLowerCase().trim();
        if (t === 'sensitive' || t === 'prohibited')
            return t;
        return 'normal';
    };
    // Build polygon name including the type so the parser can restore both name and type.
    // Matches the parser used elsewhere which expects "Name => Type:someType"
    const buildPolygonName = (name, type) => {
        return `${name} => Type:${type}`;
    };
    // Generate a stable-ish client-side temporary zone id.
    const generateZoneId = () => {
        return `zone-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    };
    useEffect(() => {
        if (liveStreamUrl) {
            // Stream is available, stop checking for stream and start updating image
            if (streamCheckInterval) {
                clearInterval(streamCheckInterval);
                setStreamCheckInterval(null);
            }
            const updateImage = () => {
                setLiveImageUrl(generateImageUrl(liveStreamUrl));
                setImageError(false);
            };
            updateImage(); // Initial load
            // Set up interval to update image every 6 seconds
            const imageInterval = setInterval(updateImage, 6000);
            setImageUpdateInterval(imageInterval);
            return () => {
                if (imageInterval) {
                    clearInterval(imageInterval);
                }
            };
        }
        else {
            // No stream URL, clear any existing image update interval
            if (imageUpdateInterval) {
                clearInterval(imageUpdateInterval);
                setImageUpdateInterval(null);
            }
            setLiveImageUrl('');
        }
    }, [liveStreamUrl]);
    // Check for live stream availability continuously until stream is found
    useEffect(() => {
        const checkLiveStream = () => {
            if (systemId && !liveStreamUrl) {
                dispatch(getLiveStreamUrl({ systemId: systemId }));
            }
        };
        if (systemId && !liveStreamUrl) {
            // Initial check
            checkLiveStream();
            // Set up interval to check continuously until stream is found
            const checkInterval = setInterval(checkLiveStream, 3000); // Check every 3 seconds
            setStreamCheckInterval(checkInterval);
            return () => {
                if (checkInterval) {
                    clearInterval(checkInterval);
                }
            };
        }
    }, [systemId, liveStreamUrl, dispatch]);
    // Cleanup intervals on unmount
    useEffect(() => {
        return () => {
            if (streamCheckInterval) {
                clearInterval(streamCheckInterval);
            }
            if (imageUpdateInterval) {
                clearInterval(imageUpdateInterval);
            }
        };
    }, []);
    // Function to fetch and refresh zones from API
    const refreshZones = async () => {
        setIsLoading(true);
        try {
            await dispatch(getCanvasFrameAndPolygon(systemId)).unwrap();
        }
        catch (error) {
            toast.error('Failed to load zones');
        }
        finally {
            setIsLoading(false);
        }
    };
    // Map backend data to zones when data is loaded
    useEffect(() => {
        if (polygons && polygons.data) {
            const mappedZones = [];
            const processedPolygonIds = new Set();
            // Helper function to parse zone name and type from backend format
            const parseZoneNameAndType = (fullName) => {
                const match = fullName.match(/^(.+?)\s*=>\s*Type:(.+)$/);
                if (match) {
                    return {
                        name: match[1].trim(),
                        type: match[2].toLowerCase().trim()
                    };
                }
                return {
                    name: fullName,
                    type: 'normal'
                };
            };
            // Map polygons from all frames to zones
            polygons.data.forEach((frame) => {
                if (frame.polygons && frame.polygons.length > 0) {
                    frame.polygons.forEach((polygon) => {
                        if (polygon.id && processedPolygonIds.has(polygon.id)) {
                            return;
                        }
                        if (polygon.id) {
                            processedPolygonIds.add(polygon.id);
                        }
                        const zoneNameSource = polygon.zoneData?.name || polygon.name;
                        const { name: parsedName, type: parsedTypeFromName } = parseZoneNameAndType(zoneNameSource);
                        const zone = {
                            id: polygon.id || `temp-${Date.now()}-${Math.random()}`,
                            name: parsedName,
                            color: polygon.color,
                            enabled: polygon.zoneData?.isEnabled ?? true,
                            pointsAdded: polygon.points.length,
                            type: polygon.polygonType?.toLowerCase() || parsedTypeFromName || 'normal',
                            polygonId: polygon.id,
                            severity: polygon.zoneData?.zonePoints || 1,
                            polygonPoints: polygon.points.map((point) => ({
                                ...point,
                                pointOrder: point.pointOrder
                            }))
                        };
                        mappedZones.push(zone);
                    });
                }
            });
            setZones(mappedZones);
        }
    }, [polygons]);
    // Initial load
    useEffect(() => {
        if (systemId) {
            refreshZones();
        }
        return () => {
            dispatch(clearCanvas());
        };
    }, [systemId]);
    const drawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx)
            return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Draw existing zones polygons
        zones.forEach((zone) => {
            if (zone.polygonPoints && zone.polygonPoints.length > 2) {
                drawPolygon(ctx, zone.polygonPoints, zone.color, false, zone.id);
            }
        });
        // Draw current drawing polygon
        if (tempPolygon.length > 0) {
            const editingZone = zones.find((z) => z.id === editingZoneId);
            const color = editingZone?.color || editingZoneData?.color || '#4CAF50';
            drawPolygon(ctx, tempPolygon, color, true, editingZoneId || undefined);
        }
    }, [
        zones,
        tempPolygon,
        editingZoneId,
        isEditingPoints,
        isDragging,
        dragPointIndex,
        editingZoneData
    ]);
    const drawPolygon = (ctx, points, color, isDrawingMode, zoneId) => {
        if (points.length < 1)
            return;
        ctx.strokeStyle = color;
        ctx.fillStyle = color + '20';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        const isEditingThisZone = isEditingPoints && editingZoneId === zoneId;
        const shouldClosePath = points.length > 2 && !isDrawingMode && !isEditingThisZone;
        if (shouldClosePath) {
            ctx.closePath();
            ctx.fill();
        }
        ctx.stroke();
        // Draw points with editing visual feedback
        points.forEach((point, index) => {
            ctx.beginPath();
            const isEditingThisZone = isEditingPoints && editingZoneId === zoneId;
            const isDraggingThisPoint = isDragging && dragPointIndex === index && editingZoneId === zoneId;
            const radius = isEditingThisZone ? (isDraggingThisPoint ? 8 : 6) : 4;
            ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
            if (isDraggingThisPoint) {
                ctx.fillStyle = '#ffaa00';
            }
            else if (isEditingThisZone) {
                ctx.fillStyle = '#ff4444';
            }
            else {
                ctx.fillStyle = color;
            }
            ctx.fill();
            // Draw point numbers
            ctx.fillStyle = 'white';
            ctx.font = '10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText((index + 1).toString(), point.x, point.y + 3);
        });
    };
    useEffect(() => {
        drawCanvas();
    }, [drawCanvas]);
    // Helper function to check if a click is near a point
    const getClickedPointIndex = (clickX, clickY, points) => {
        const threshold = 10;
        for (let i = 0; i < points.length; i++) {
            const distance = Math.sqrt(Math.pow(clickX - points[i].x, 2) + Math.pow(clickY - points[i].y, 2));
            if (distance <= threshold) {
                return i;
            }
        }
        return -1;
    };
    const handleCanvasMouseDown = (event) => {
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        // Handle point editing mode
        if (isEditingPoints && editingZoneId && editingZoneData?.polygonPoints) {
            const clickedPointIndex = getClickedPointIndex(x, y, editingZoneData.polygonPoints);
            if (clickedPointIndex !== -1) {
                const clickedPoint = editingZoneData.polygonPoints[clickedPointIndex];
                if (event.button === 2) {
                    event.preventDefault();
                    deletePointAtIndex(clickedPointIndex);
                    setHasUnsavedChanges(true); // Mark as having unsaved changes
                    return;
                }
                else if (event.button === 0) {
                    setIsDragging(true);
                    setDragPointIndex(clickedPointIndex);
                    setDragOffset({
                        x: x - clickedPoint.x,
                        y: y - clickedPoint.y
                    });
                    return;
                }
            }
            else if (event.button === 0) {
                const newPoint = {
                    x: Math.round(x),
                    y: Math.round(y),
                    pointOrder: editingZoneData.polygonPoints.length
                };
                addPointToZone(newPoint);
                setHasUnsavedChanges(true); // Mark as having unsaved changes
                return;
            }
        }
        // Handle normal polygon drawing mode
        if (isDrawing && editingZoneId && event.button === 0) {
            const newPoint = {
                x: Math.round(x),
                y: Math.round(y),
                pointOrder: tempPolygon.length
            };
            setTempPolygon([...tempPolygon, newPoint]);
            setHasUnsavedChanges(true); // Mark as having unsaved changes
        }
    };
    const handleCanvasMouseMove = (event) => {
        if (!isDragging || dragPointIndex === null || !editingZoneId || !editingZoneData)
            return;
        const canvas = canvasRef.current;
        if (!canvas)
            return;
        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        if (editingZoneData.polygonPoints) {
            const updatedPoints = editingZoneData.polygonPoints.map((point, index) => index === dragPointIndex
                ? { ...point, x: Math.round(x - dragOffset.x), y: Math.round(y - dragOffset.y) }
                : point);
            setEditingZoneData({
                ...editingZoneData,
                polygonPoints: updatedPoints
            });
            setZones(zones.map((zone) => zone.id === editingZoneId ? { ...zone, polygonPoints: updatedPoints } : zone));
            setHasUnsavedChanges(true); // Mark as having unsaved changes when dragging points
        }
    };
    const handleCanvasMouseUp = () => {
        if (isDragging) {
            setIsDragging(false);
            setDragPointIndex(null);
            setDragOffset({ x: 0, y: 0 });
        }
    };
    const handleCanvasContextMenu = (event) => {
        event.preventDefault();
    };
    const startDrawing = () => {
        setIsDrawing(true);
        setTempPolygon([]);
    };
    const finishDrawing = () => {
        if (tempPolygon.length < 3) {
            toast.error('Please draw at least 3 points to create a polygon');
            return;
        }
        if (editingZoneData) {
            setEditingZoneData({
                ...editingZoneData,
                polygonPoints: tempPolygon
            });
        }
        setZones(zones.map((zone) => zone.id === editingZoneId
            ? {
                ...zone,
                polygonPoints: tempPolygon,
                pointsAdded: tempPolygon.length
            }
            : zone));
        setIsDrawing(false);
        setTempPolygon([]);
        setHasUnsavedChanges(true); // Mark as having unsaved changes
    };
    const clearPolygon = () => {
        setTempPolygon([]);
        if (editingZoneId) {
            setZones(zones.map((zone) => zone.id === editingZoneId ? { ...zone, polygonPoints: [], pointsAdded: 0 } : zone));
            setHasUnsavedChanges(true); // Mark as having unsaved changes
        }
    };
    const togglePointEditing = async (zoneId) => {
        if (isEditingPoints && editingZoneId === zoneId) {
            if (editingZoneData?.polygonPoints && editingZoneData.polygonPoints.length >= 3) {
                await handleSaveZone();
            }
            else {
                setIsEditingPoints(false);
                setEditingZoneId(null);
                setEditingZoneData(null);
            }
        }
        else {
            const currentZone = zones.find((z) => z.id === zoneId);
            if (currentZone) {
                setEditingZoneData({
                    name: currentZone.name,
                    type: currentZone.type || 'normal',
                    severity: currentZone.severity || 1,
                    color: currentZone.color,
                    polygonPoints: currentZone.polygonPoints ? [...currentZone.polygonPoints] : []
                });
                setIsDrawing(false);
                setIsEditingPoints(true);
                setEditingZoneId(zoneId);
                setTempPolygon([]);
            }
        }
    };
    const deletePointAtIndex = (pointIndex) => {
        if (!isEditingPoints || !editingZoneId || !editingZoneData?.polygonPoints)
            return;
        if (editingZoneData.polygonPoints.length <= 3) {
            toast.warn('Cannot delete point. Polygon must have at least 3 points.');
            return;
        }
        const updatedPoints = editingZoneData.polygonPoints
            .filter((_, index) => index !== pointIndex)
            .map((point, index) => ({ ...point, pointOrder: index }));
        setEditingZoneData({
            ...editingZoneData,
            polygonPoints: updatedPoints
        });
        setZones(zones.map((zone) => zone.id === editingZoneId
            ? { ...zone, polygonPoints: updatedPoints, pointsAdded: updatedPoints.length }
            : zone));
        setHasUnsavedChanges(true); // Already marked in handleCanvasMouseDown but added for clarity
    };
    const addPointToZone = (newPoint) => {
        if (!isEditingPoints || !editingZoneId || !editingZoneData)
            return;
        const updatedPoints = [
            ...(editingZoneData.polygonPoints || []),
            { ...newPoint, pointOrder: editingZoneData.polygonPoints?.length || 0 }
        ];
        setEditingZoneData({
            ...editingZoneData,
            polygonPoints: updatedPoints
        });
        setZones(zones.map((zone) => zone.id === editingZoneId
            ? { ...zone, polygonPoints: updatedPoints, pointsAdded: updatedPoints.length }
            : zone));
        setHasUnsavedChanges(true); // Already marked in handleCanvasMouseDown but added for clarity
    };
    const handleDeleteZone = async (zoneId) => {
        const zoneToDelete = zones.find((zone) => zone.id === zoneId);
        if (!zoneToDelete?.polygonId) {
            toast.error('Cannot delete unsaved zone');
            return;
        }
        setIsDeleting(true); // Set deleting state to true
        try {
            await dispatch(deleteCanvasPolygon(zoneToDelete.polygonId)).unwrap();
            toast.success('Zone deleted successfully');
            await refreshZones();
            if (expandedZone === zoneId) {
                setExpandedZone(false);
            }
        }
        catch (error) {
            console.error('Failed to delete zone:', error);
            toast.error('Failed to delete zone');
        }
        finally {
            setIsDeleting(false); // Reset deleting state
        }
    };
    const handleAddZone = () => {
        // Check for unsaved changes before adding new zone
        if (hasUnsavedChanges && editingZoneId) {
            toast.warn('Please save your current changes before adding a new zone.');
            return;
        }
        if (isDrawing || isEditing) {
            toast.warn('Please finish editing the current zone before adding a new one.');
            return;
        }
        const colors = ['#4CAF50', '#F44336', '#FF9800', '#9C27B0', '#2196F3', '#E91E63'];
        const usedColors = zones.map((zone) => zone.color);
        const availableColors = colors.filter((color) => !usedColors.includes(color));
        const selectedColor = availableColors[0] ||
            `#${Math.floor(Math.random() * 16777215)
                .toString(16)
                .padStart(6, '0')}`;
        const newId = generateZoneId();
        const zoneNumber = zones.length + 1;
        setEditingZoneData({
            name: `Zone ${zoneNumber}`,
            type: 'normal',
            severity: 1,
            color: selectedColor,
            polygonPoints: []
        });
        const newZone = {
            id: newId,
            name: `Zone ${zoneNumber}`,
            color: selectedColor,
            enabled: true,
            pointsAdded: 0,
            type: 'normal',
            severity: 1,
            polygonPoints: []
        };
        setZones([newZone, ...zones]);
        setExpandedZone(newId);
        setIsEditing(true);
        setEditingZoneId(newId);
        startDrawing();
        setHasUnsavedChanges(false); // Reset unsaved changes for new zone
    };
    const handleSaveZone = async () => {
        if (isSaving || !editingZoneId || !editingZoneData) {
            return;
        }
        const currentZone = zones.find((z) => z.id === editingZoneId);
        if (!currentZone)
            return;
        let preparedPoints = [];
        if (isDrawing && tempPolygon.length >= 3) {
            preparedPoints = tempPolygon.map((point, index) => ({
                ...point,
                pointOrder: index
            }));
        }
        else if (editingZoneData.polygonPoints && editingZoneData.polygonPoints.length >= 3) {
            preparedPoints = editingZoneData.polygonPoints.map((point, index) => ({
                ...point,
                pointOrder: index
            }));
        }
        else {
            toast.warn('Please draw at least 3 points to create a polygon');
            return;
        }
        const trimmedName = editingZoneData.name.trim();
        if (!trimmedName) {
            toast.error('Zone name is required');
            return;
        }
        setIsSaving(true);
        try {
            const formattedType = formatZoneType(editingZoneData.type);
            const polygonNameWithType = buildPolygonName(trimmedName, formattedType);
            const pointsPayload = preparedPoints.map(({ x, y, pointOrder }) => ({
                x,
                y,
                pointOrder
            }));
            if (currentZone.polygonId) {
                let backendPolygon;
                if (polygons?.data) {
                    for (const frame of polygons.data) {
                        if (frame.polygons) {
                            backendPolygon = frame.polygons.find((p) => p.id === currentZone.polygonId);
                            if (backendPolygon)
                                break;
                        }
                    }
                }
                if (backendPolygon) {
                    const updateCommand = {
                        frameId: backendPolygon.frameId,
                        frameSystemId: systemId,
                        polygon: {
                            ...backendPolygon,
                            id: currentZone.polygonId,
                            name: polygonNameWithType,
                            polygonType: editingZoneData.type,
                            color: currentZone.color,
                            points: pointsPayload,
                            zoneData: {
                                name: polygonNameWithType,
                                color: currentZone.color,
                                isEnabled: currentZone.enabled,
                                zonePoints: editingZoneData.severity
                            }
                        }
                    };
                    await dispatch(updateCanvasPolygon(updateCommand)).unwrap();
                    toast.success('Zone updated successfully');
                }
            }
            else {
                const image = imageRef.current;
                if (image && systemId) {
                    const polygon = {
                        systemId: systemId,
                        name: polygonNameWithType,
                        color: currentZone.color,
                        polygonType: editingZoneData.type,
                        points: pointsPayload,
                        zoneData: {
                            name: polygonNameWithType,
                            color: currentZone.color,
                            isEnabled: true,
                            zonePoints: editingZoneData.severity
                        }
                    };
                    const frameDto = {
                        systemId: systemId,
                        name: `Frame_${new Date().getTime()}`,
                        color: '#000000',
                        width: image.offsetWidth,
                        height: image.offsetHeight,
                        polygons: [polygon]
                    };
                    await dispatch(addCanvasFrameAndPolygon(frameDto)).unwrap();
                    toast.success('Zone saved successfully');
                }
            }
            await refreshZones();
            setIsEditing(false);
            setEditingZoneId(null);
            setEditingZoneData(null);
            setExpandedZone(false);
            setIsDrawing(false);
            setTempPolygon([]);
            setIsEditingPoints(false);
            setHasUnsavedChanges(false); // Reset unsaved changes after successful save
        }
        catch (error) {
            console.error('Failed to save zone:', error);
            toast.error('Failed to save zone');
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleCancelZone = async (zoneId) => {
        // Check for unsaved changes before canceling
        // if (hasUnsavedChanges && editingZoneId === zoneId) {
        //   toast.warn('You have unsaved changes. Please save your work before canceling.');
        //   return;
        // }
        const zone = zones.find((z) => z.id === zoneId);
        if (zone && !zone.polygonId) {
            setZones(zones.filter((z) => z.id !== zoneId));
        }
        else {
            await refreshZones();
        }
        setIsEditing(false);
        setEditingZoneId(null);
        setEditingZoneData(null);
        setExpandedZone(false);
        setIsDrawing(false);
        setTempPolygon([]);
        setIsEditingPoints(false);
        setHasUnsavedChanges(false); // Reset unsaved changes
    };
    const handleZoneNameChange = (newName) => {
        if (editingZoneData) {
            setEditingZoneData({ ...editingZoneData, name: newName });
            setHasUnsavedChanges(true); // Mark as having unsaved changes
        }
    };
    const handleZoneTypeChange = (newType) => {
        if (editingZoneData) {
            setEditingZoneData({ ...editingZoneData, type: newType });
            setHasUnsavedChanges(true); // Mark as having unsaved changes
        }
    };
    const handleZoneSeverityChange = (newSeverity) => {
        // Apply constraints: must be between 1 and 100
        if (newSeverity < 1)
            newSeverity = 1;
        if (newSeverity > 100)
            newSeverity = 100;
        if (editingZoneData) {
            setEditingZoneData({ ...editingZoneData, severity: newSeverity });
            setHasUnsavedChanges(true); // Mark as having unsaved changes
        }
    };
    const handleAccordionChange = (zoneId) => (event, isExpanded) => {
        // Check if we're collapsing a zone with unsaved changes
        if (!isExpanded && editingZoneId === zoneId && hasUnsavedChanges) {
            toast.warn('You have unsaved changes. Please save your work before closing.');
            event.preventDefault();
            return;
        }
        // Check if trying to expand another zone while having unsaved changes
        if (isExpanded && editingZoneId && editingZoneId !== zoneId && hasUnsavedChanges) {
            toast.warn('Please save your current changes before switching to another zone.');
            event.preventDefault();
            return;
        }
        if (isDrawing && editingZoneId !== null && editingZoneId !== zoneId) {
            toast.warn('Please finish drawing and save the current polygon before switching zones.');
            return;
        }
        setExpandedZone(isExpanded ? zoneId : false);
    };
    const isSaveButtonDisabled = (zone) => {
        if (isSaving)
            return true;
        if (zone.id !== editingZoneId)
            return true;
        if (!editingZoneData)
            return true;
        if (isDrawing && tempPolygon.length < 3)
            return true;
        if (!isDrawing && (!editingZoneData.polygonPoints || editingZoneData.polygonPoints.length < 3))
            return true;
        return false;
    };
    const handleGoBack = () => {
        navigate(`/detail-asset/${assetId}`);
    };
    // Add a cleanup effect to warn users before leaving the page with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);
    return (_jsxs(Box, { sx: containerStyles, children: [_jsxs(Box, { sx: headerStyles, children: [_jsx(IconButton, { onClick: handleGoBack, sx: { mr: 1 }, children: _jsx(ArrowBackIosNewRounded, {}) }), _jsxs(Typography, { variant: 'h6', sx: breadcrumbStyles, children: ["Assets / Asset 1 /", ' ', _jsx("span", { style: { color: theme.palette.text.primary }, children: "PLR0992F Live Feed" })] })] }), _jsxs(Grid, { container: true, spacing: 2, sx: { height: 'calc(100vh - 130px)' }, children: [_jsx(Grid, { size: { xs: 12, md: 9 }, sx: { display: 'flex', flexDirection: 'column' }, children: _jsx(Box, { sx: { flex: 1, display: 'flex', flexDirection: 'column' }, children: _jsx(Box, { sx: { ...videoContainerStyles, flex: 1, display: 'flex' }, children: _jsxs(Box, { sx: { ...videoBoxStyles, flex: 1, height: 'calc(100dvh - 200px)' }, children: [liveImageUrl ? (_jsx("img", { ref: imageRef, src: liveImageUrl, alt: 'Live Feed', style: videoImageStyles, onLoad: () => {
                                                const img = imageRef.current;
                                                const canvas = canvasRef.current;
                                                if (img && canvas) {
                                                    canvas.width = img.offsetWidth;
                                                    canvas.height = img.offsetHeight;
                                                    drawCanvas();
                                                }
                                            }, onError: () => {
                                                // If live stream fails, fall back to error state
                                                setImageError(true);
                                                // Optionally set a fallback image:
                                                // setFallbackImageUrl(generateRandomImageUrl());
                                            } })) : (_jsx(Box, { sx: {
                                                backgroundColor: theme.palette.action.disabled,
                                                height: 552,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                                overflow: 'hidden',
                                                borderRadius: '6px'
                                            }, children: _jsx(Typography, { variant: 'subtitle1', color: 'white', children: "Connection not available" }) })), _jsx("canvas", { ref: canvasRef, onMouseDown: handleCanvasMouseDown, onMouseMove: handleCanvasMouseMove, onMouseUp: handleCanvasMouseUp, onContextMenu: handleCanvasContextMenu, style: {
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                cursor: isDragging
                                                    ? 'grabbing'
                                                    : isEditingPoints || isDrawing
                                                        ? 'crosshair'
                                                        : 'default',
                                                pointerEvents: isDrawing || isEditingPoints ? 'auto' : 'none'
                                            } }), isDrawing && (_jsxs(Box, { sx: {
                                                position: 'absolute',
                                                top: 10,
                                                left: 10,
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '8px 12px',
                                                borderRadius: 1,
                                                fontSize: '14px',
                                                zIndex: 10
                                            }, children: ["Click to add points \u2022 ", tempPolygon.length, " points drawn", tempPolygon.length >= 3 && ' • Click Save to finish'] })), _jsx(Box, { sx: timestampStyles, children: new Date().toLocaleString('en-US', {
                                                month: 'short',
                                                day: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                            }) })] }) }) }) }), _jsx(Grid, { size: { xs: 12, md: 3 }, children: _jsxs(Card, { sx: zonesCardStyles, children: [_jsxs(Box, { sx: { ...between, ...zonesHeaderStyles }, children: [_jsx(Typography, { variant: 'subtitle1', children: "Zones" }), _jsx(Box, { sx: { ...colCenter, gap: 1 }, children: _jsx(IconButton, { size: 'small', sx: addButtonStyles, onClick: handleAddZone, disabled: isDrawing || isEditing || liveImageUrl === null || liveImageUrl === '', children: _jsx(AddRounded, { fontSize: 'small' }) }) })] }), isEditingPoints && (_jsxs(Box, { sx: {
                                        p: 1,
                                        bgcolor: 'info.light',
                                        borderRadius: 1,
                                        mb: 1,
                                        border: '1px solid',
                                        borderColor: 'info.main'
                                    }, children: [_jsx(Typography, { variant: 'caption', fontWeight: 'bold', color: 'info.dark', children: "Point Editing Mode" }), _jsxs(Typography, { variant: 'body2', color: 'info.dark', sx: { mt: 0.5, fontSize: '0.75rem' }, children: ["\u2022 Left click & drag points to move them", _jsx("br", {}), "\u2022 Right click points to delete them", _jsx("br", {}), "\u2022 Left click empty space to add new points", _jsx("br", {}), "\u2022 Polygon stays open for continuous editing"] })] })), _jsx(Box, { sx: zoneListStyles, children: zones.map((zone) => (_jsxs(Accordion, { expanded: expandedZone === zone.id, onChange: handleAccordionChange(zone.id), sx: accordionStyles, children: [_jsx(AccordionSummary, { sx: accordionSummaryStyles, children: _jsxs(Box, { sx: { ...between, ...accordionContentStyles }, children: [_jsx(Box, { sx: zoneInfoStyles, children: _jsx(Typography, { variant: 'body2', fontWeight: 500, children: zone.id === editingZoneId && editingZoneData
                                                                    ? editingZoneData.name
                                                                    : zone.name }) }), _jsxs(Box, { sx: zoneRightSideStyles, children: [_jsx(Box, { sx: colorBoxStyles(zone.color) }), _jsx(IconButton, { size: 'small', color: 'error', disabled: !zone.polygonId || isDeleting, onClick: (e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteZone(zone.id);
                                                                    }, children: _jsx(Delete24Regular, {}) }), _jsx(ExpandMoreRounded, { sx: expandIconStyles(expandedZone === zone.id) })] })] }) }), _jsxs(AccordionDetails, { sx: accordionDetailsStyles, children: [_jsxs(Box, { sx: { ...between, ...detailRowStyles }, children: [_jsx(Typography, { variant: 'caption', sx: detailLabelStyles, children: "Name" }), _jsx(TextField, { variant: 'outlined', size: 'small', value: zone.id === editingZoneId && editingZoneData
                                                                    ? editingZoneData.name
                                                                    : zone.name, onChange: (e) => handleZoneNameChange(e.target.value), disabled: zone.id !== editingZoneId || !editingZoneData, sx: textFieldStyles, inputProps: { maxLength: 40 }, helperText: `${editingZoneData?.name?.length || 0}/40 characters` })] }), _jsxs(Box, { sx: { ...between, ...detailRowStyles }, children: [_jsx(Typography, { variant: 'caption', sx: detailLabelStyles, children: "Type" }), _jsx(FormControl, { size: 'small', sx: formControlStyles, children: _jsxs(Select, { variant: 'outlined', size: 'small', value: zone.id === editingZoneId && editingZoneData
                                                                        ? editingZoneData.type
                                                                        : zone.type || 'normal', onChange: (e) => handleZoneTypeChange(e.target.value), disabled: zone.id !== editingZoneId || !editingZoneData, sx: selectStyles, children: [_jsx(MenuItem, { value: 'normal', children: "Normal" }), _jsx(MenuItem, { value: 'sensitive', children: "Sensitive" }), _jsx(MenuItem, { value: 'prohibited', children: "Prohibited" })] }) })] }), _jsxs(Box, { sx: { ...between, ...detailRowStyles }, children: [_jsx(Typography, { variant: 'caption', sx: detailLabelStyles, children: "Severity" }), _jsx(TextField, { variant: 'outlined', type: 'number', size: 'small', value: zone.id === editingZoneId && editingZoneData
                                                                    ? editingZoneData.severity
                                                                    : zone.severity || 1, onChange: (e) => handleZoneSeverityChange(Number(e.target.value)), disabled: zone.id !== editingZoneId || !editingZoneData, inputProps: { min: 1, max: 100 }, sx: textFieldStyles })] }), _jsxs(Box, { sx: { my: 1.5 }, children: [_jsxs(Typography, { variant: 'caption', sx: { color: theme.palette.text.secondary, fontWeight: 500 }, children: ["Polygon Points (", zone.polygonPoints?.length || tempPolygon.length || 0, ")"] }), zone.id === editingZoneId && isDrawing ? (_jsxs(Box, { sx: { mt: 1 }, children: [_jsx(Typography, { variant: 'body2', color: 'textSecondary', sx: { mb: 1 }, children: "Click on the image to draw polygon points" }), _jsx(Box, { sx: { display: 'flex', gap: 1, mb: 1 }, children: _jsx(Button, { variant: 'outlined', size: 'small', onClick: clearPolygon, disabled: tempPolygon.length === 0, children: "Clear Points" }) })] })) : (_jsx(Box, { sx: { mt: 1 }, children: (zone.polygonPoints?.length || 0) > 0 ? (_jsxs(Box, { children: [_jsxs(Typography, { variant: 'body2', color: 'textPrimary', children: [zone.polygonPoints?.length, " points defined"] }), _jsx(Button, { variant: 'outlined', size: 'small', onClick: () => togglePointEditing(zone.id), sx: { mt: 1 }, disabled: isDrawing ||
                                                                                (isEditingPoints && editingZoneId !== zone.id) ||
                                                                                (isEditingPoints && editingZoneId === zone.id), children: "Edit Polygon" })] })) : (_jsx(Button, { variant: 'outlined', size: 'small', onClick: () => {
                                                                        if (isDrawing || isEditing) {
                                                                            toast.warn('Please finish drawing and save the current polygon before starting a new one.');
                                                                            return;
                                                                        }
                                                                        const currentZone = zones.find((z) => z.id === zone.id);
                                                                        if (currentZone) {
                                                                            setEditingZoneData({
                                                                                name: currentZone.name,
                                                                                type: currentZone.type || 'normal',
                                                                                severity: currentZone.severity || 1,
                                                                                color: currentZone.color,
                                                                                polygonPoints: []
                                                                            });
                                                                        }
                                                                        setEditingZoneId(zone.id);
                                                                        setIsEditing(true);
                                                                        startDrawing();
                                                                    }, disabled: isDrawing || isEditing, children: "Draw Polygon" })) }))] }), _jsxs(Box, { sx: { ...colCenter, gap: 1.5, mt: 1.5 }, children: [_jsx(Button, { variant: 'outlined', size: 'small', color: 'inherit', fullWidth: true, onClick: () => handleCancelZone(zone.id), children: "Cancel" }), _jsx(Button, { variant: 'contained', size: 'small', color: 'inherit', fullWidth: true, onClick: handleSaveZone, disabled: isSaveButtonDisabled(zone), children: _jsxs(Box, { sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        gap: 1
                                                                    }, children: [isSaving && zone.id === editingZoneId && (_jsx(CircularProgress, { size: 16, color: 'inherit', thickness: 5 })), zone.polygonId ? 'Update' : 'Save'] }) })] })] })] }, zone.id))) })] }) })] })] }));
};
